import React, { useCallback, useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';

// Migration 258/259: who sees which goal is decided by the database (dashboard_goals_for_me()); this panel only shows what comes
// back, grouped Company / Team / Mine / People I lead (people who report to the viewer, directly or further down the
// reports-to chain) / Other people (individual goals an Owner or Admin can see), with Edit/Remove only where can_edit is true.
export const GOAL_SECTIONS = [
    { key: 'company', title: 'Company', hint: 'Everyone in the company sees these.' },
    { key: 'team', title: 'Team', hint: 'Goals for your team or group.' },
    { key: 'mine', title: 'Mine', hint: 'Set for you by your supervisor.' },
    { key: 'reports', title: 'People I lead', hint: 'Goals for the people who report to you.' },
    { key: 'others', title: 'Other people', hint: 'Individual goals you can see as an Owner or Admin.' },
];

const METRIC_LABELS = {
    revenue_collected: 'Revenue collected',
    revenue_invoiced: 'Revenue invoiced',
    engagements_completed: 'Trips completed',
    new_accounts: 'New accounts',
};
const PERIOD_LABELS = { week: 'this week', month: 'this month', quarter: 'this quarter', year: 'this year' };

export function goalSection(goal) {
    if (goal.scope === 'company') return 'company';
    if (goal.scope === 'team') return 'team';
    if (goal.mine) return 'mine';
    return goal.reports_to_me ? 'reports' : 'others';
}

function formatTarget(goal) {
    const value = Number(goal.target_value);
    if (goal.metric_key && goal.metric_key.startsWith('revenue')) {
        return value.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
    }
    return value.toLocaleString();
}

function GoalRow({ goal, progress, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [target, setTarget] = useState(String(goal.target_value));
    const [label, setLabel] = useState(goal.label || '');
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const save = async () => {
        const value = Number(target);
        if (!Number.isFinite(value) || value <= 0) {
            setError('Enter a target above zero.');
            return;
        }
        setBusy(true);
        const { error: saveError } = await supabaseClient.from('dashboard_goals')
            .update({ target_value: value, label: label.trim() || null })
            .eq('id', goal.id);
        setBusy(false);
        if (saveError) {
            setError(saveError.message);
            return;
        }
        setError(null);
        setEditing(false);
        onSaved();
    };

    const remove = async () => {
        if (!window.confirm('Remove this goal?')) return;
        setBusy(true);
        const { error: removeError } = await supabaseClient.from('dashboard_goals').delete().eq('id', goal.id);
        setBusy(false);
        if (removeError) {
            setError(removeError.message);
            return;
        }
        onSaved();
    };

    const name = goal.label || METRIC_LABELS[goal.metric_key] || goal.metric_key;
    return (
        <li data-goal-id={goal.id} className="py-2 border-b last:border-0 border-[var(--surface-border)]">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="font-semibold text-[var(--ink)] truncate">{name}</div>
                    <div className="text-sm text-[var(--ink-muted)]">
                        {METRIC_LABELS[goal.metric_key] || goal.metric_key}: {formatTarget(goal)} {PERIOD_LABELS[goal.period] || ''}
                        {goal.group_name ? ` · ${goal.group_name}` : ''}
                        {goal.staff_name && !goal.mine ? ` · ${goal.staff_name}` : ''}
                    </div>
                    {progress != null && (
                        <div className="text-xs text-[var(--accent-ink)]">{Number(progress).toLocaleString()}% reached</div>
                    )}
                </div>
                {goal.can_edit && !editing && (
                    <div className="flex gap-2 shrink-0">
                        <button type="button" onClick={() => setEditing(true)} disabled={busy}
                            className="text-sm font-medium text-[var(--accent-ink)] hover:text-[var(--accent-ink-hover)]">Edit</button>
                        <button type="button" onClick={remove} disabled={busy}
                            className="text-sm font-medium text-[var(--ui-alert)]">Remove</button>
                    </div>
                )}
            </div>
            {editing && (
                <div className="mt-2 flex flex-wrap items-end gap-2">
                    <label className="text-xs text-[var(--ink-muted)] flex flex-col">
                        Name
                        <input value={label} onChange={(e) => setLabel(e.target.value)}
                            className="px-2 py-1 text-sm rounded border bg-[var(--surface-sunken)] border-[var(--surface-border)] text-[var(--ink)]" />
                    </label>
                    <label className="text-xs text-[var(--ink-muted)] flex flex-col">
                        Target
                        <input type="number" min="0" step="any" value={target} onChange={(e) => setTarget(e.target.value)}
                            className="w-28 px-2 py-1 text-sm rounded border bg-[var(--surface-sunken)] border-[var(--surface-border)] text-[var(--ink)]" />
                    </label>
                    <button type="button" onClick={save} disabled={busy}
                        className="px-3 py-1 text-sm font-medium rounded-lg bg-[var(--btn-fill)] text-[var(--btn-ink)]">Save</button>
                    <button type="button" onClick={() => { setEditing(false); setError(null); }} disabled={busy}
                        className="px-3 py-1 text-sm font-medium rounded-lg border border-[var(--surface-border)] text-[var(--ink)]">Cancel</button>
                </div>
            )}
            {error && <p role="alert" className="mt-1 text-xs text-[var(--ui-alert)]">{error}</p>}
        </li>
    );
}

function DashboardGoalsPanel({ showProgress = false }) {
    const [goals, setGoals] = useState(null);
    const [progress, setProgress] = useState({});
    const [error, setError] = useState(null);
    const [managerName, setManagerName] = useState(null);

    const load = useCallback(async () => {
        const { data, error: loadError } = await supabaseClient.rpc('dashboard_goals_for_me');
        if (loadError) {
            setError(loadError.message);
            setGoals([]);
            return;
        }
        const rows = Array.isArray(data) ? data : [];
        setError(null);
        setGoals(rows);
        // Everyone can see who they report to (migration 259), shown beside their own goals.
        if (rows.some((goal) => goal.mine)) {
            const { data: me } = await supabaseClient.rpc('staff_my_reports_to');
            const row = Array.isArray(me) ? me[0] : me;
            setManagerName(row?.reports_to_name || null);
        }
        // Progress uses the money and trip numbers the viewer may already see; without that access it stays hidden rather
        // than showing a misleading 0%.
        if (showProgress && rows.length > 0) {
            const { data: gap } = await supabaseClient.from('dashboard_goal_gap').select('goal_id, attainment_pct');
            setProgress(Object.fromEntries((Array.isArray(gap) ? gap : []).map((row) => [row.goal_id, row.attainment_pct])));
        } else {
            setProgress({});
        }
    }, [showProgress]);

    useEffect(() => { load(); }, [load]);

    if (goals === null) return null;
    const bySection = GOAL_SECTIONS.map((section) => ({
        ...section,
        goals: goals.filter((goal) => goalSection(goal) === section.key),
    })).filter((section) => section.goals.length > 0);

    return (
        <section data-m6-layout="dashboard" data-m6-component="DashboardGoalsPanel"
            className="rounded-xl shadow-md p-6 bg-[var(--surface-raised)] text-[var(--ink)]">
            <h2 className="text-xl font-bold mb-4">Goals</h2>
            {error && <p role="alert" className="text-sm text-[var(--ui-alert)]">Could not load goals: {error}</p>}
            {!error && bySection.length === 0 && (
                <p className="text-sm text-[var(--ink-muted)]">No goals have been set for you yet.</p>
            )}
            <div className="grid gap-4 md:grid-cols-2">
                {bySection.map((section) => (
                    <div key={section.key} data-goal-section={section.key}>
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-[var(--ink-muted)]">{section.title}</h3>
                        <p className="text-xs text-[var(--ink-muted)] mb-1">
                            {section.key === 'mine' && managerName ? `Set for you by your manager, ${managerName}, or above.` : section.hint}
                        </p>
                        <ul>
                            {section.goals.map((goal) => (
                                <GoalRow key={goal.id} goal={goal} progress={progress[goal.id]} onSaved={load} />
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </section>
    );
}

export default DashboardGoalsPanel;
